import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getIncomeAttributesBySnapshotId, putIncomeAttributesBySnapshotId } from "../../redux/actions/incomeAttributes/incomeAttributes";
import { getLookups } from "../../redux/actions/lookups/lookups";
import { IncomeAttributeState } from "../../redux/reducers/getIncomeAttributes/getIncomeAttributesReducer";
import { LookupsState } from "../../redux/reducers/getLookups/lookupsReducer";
import { ProfileOverviewState } from "../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from "../../redux/reducers/getSaveData/saveResultReducer";
import IncomeAttributeLookupObject from "../../types/lookups/IncomeAttributeLookupObject";
import calculateIncomeAttributesTotal from "../../utils/calculateIncomeAttributesTotal";
import Total from "../../components/common/Total/Total";
import "./PIE.css";
import ErrorMessages from "../../components/common/ErrorMessages/ErrorMessages";
import { useNavigation } from "../../utils/context/NavigationContext";
import { useFormErrorContext } from "../../utils/context/FormErrorContext";
import CapitalCostsSummaryTab from "./CapitalCostsSummaryTab";
import PreviousCampaignIncomeAttributes from "../../components/common/PreviousCampaignIncomeAttributes/PreviousCampaignIncomeAttributes";
import { accordionDisplay, getCurrentCampaignYear, getLastYearSubmittedCampaign, shouldDisplayPreviousTwoYearAttributes, shouldDisplayPreviousYearAttributes } from "../../utils/previousIncomeAttributes";
import { MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID } from "../../redux/actionTypes/actionsTypes";
import { selectGetIncomeAttributes, selectLookups, selectMenu, selectPreviousIncomeAttributes, selectProfileGetOverview, selectSaveData, selectSaveResult } from "../../redux/selectors/exportSelectorPaths";
import AccordionItem, { DefaultAccordionProviderOptions } from "../../components/common/AccordionItem/AccordionItem";
import { ControlledAccordion, useAccordionProvider } from "@szhsin/react-accordion";

function CapitalCostsSummary(): ReactElement {
    const { t } = useTranslation(['pie', 'navigation']);
    const dispatch = useDispatch();
    const incomeAttributesState: IncomeAttributeState = useSelector(selectGetIncomeAttributes);
    const previousIncomeAttributesState: IncomeAttributeState = useSelector(selectPreviousIncomeAttributes)
    const lookupState: LookupsState = useSelector(selectLookups);
    const menuState = useSelector(selectMenu);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);
    const saveData: SaveDataState = useSelector(selectSaveData);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const lookupType = profileOverview.profile.profile_type_code.toLowerCase() + "-income-attributes";
    const [total, setTotal] = useState<number | undefined>(undefined)
    const [previousTotals, setPreviousTotals] = useState<Record<string, number>>({});
    const currentSnapshotId = Number(sessionStorage.getItem("currentSnapshotId") as string)
    const { direction } = useNavigation()
    const { errors, clearErrors } = useFormErrorContext()
    const providerValue = useAccordionProvider(DefaultAccordionProviderOptions)
    const isInternal = (sessionStorage.getItem("isInternalUser") as string) === "true"
    const currentCampaignYear = getCurrentCampaignYear()

    const getIncomeAttributes = () => {
        if ((!incomeAttributesState.loading && !incomeAttributesState.success)
            || incomeAttributesState.snapshotId !== Number(sessionStorage.getItem('currentSnapshotId') as string)) {
            dispatch(getIncomeAttributesBySnapshotId(sessionStorage.getItem('currentSnapshotId') as string));
        }

        const campaign = getLastYearSubmittedCampaign(profileOverview.profile)

        if (campaign && ((!previousIncomeAttributesState.loading && !previousIncomeAttributesState.success) || previousIncomeAttributesState.snapshotId !== campaign.currentSnapshotId)) {
            dispatch(getIncomeAttributesBySnapshotId(campaign.currentSnapshotId, MANAGE_PREVIOUS_INCOME_ATTRIBUTES_BY_SNAPSHOT_ID))
        }
    }

    useEffect(() => {
        getIncomeAttributes()
        // eslint-disable-next-line
    }, []);

    const getPreviousTotals = useCallback((year: string) => {
        const sessionStoragePreviousSnapshotData = sessionStorage.getItem(isInternal ? "previousSnapshotDataInternal" + year : "previousSnapshotData")
        if (sessionStoragePreviousSnapshotData !== null && sessionStoragePreviousSnapshotData !== undefined) {
            let previousSnapshotData = { incomeAttribute: [] }
            previousSnapshotData = JSON.parse(sessionStoragePreviousSnapshotData)
            if (previousSnapshotData.incomeAttribute) {
                return calculateIncomeAttributesTotal(lookupState.lookups[3]["cost"] as IncomeAttributeLookupObject[],
                    previousSnapshotData.incomeAttribute as Record<string, unknown>[])
            }
        }
        return null
    }, [lookupState.lookups, isInternal])

    useEffect(() => {
        if (!incomeAttributesState.loading && incomeAttributesState.success
            && !lookupState.loading && lookupState.success
            && lookupState.lookups[3] && lookupState.lookups[3]["cost"]) {
            setTotal(calculateIncomeAttributesTotal(lookupState.lookups[3]["cost"] as IncomeAttributeLookupObject[],
                incomeAttributesState.data as Record<string, unknown>[]))
        }
    }, [incomeAttributesState, lookupState])

    useEffect(() => {
        let tempPreviousTotals = {}
        if (shouldDisplayPreviousYearAttributes()) {
            const previousYear = getCurrentCampaignYear() - 1
            tempPreviousTotals = { [previousYear]: getPreviousTotals(String(previousYear)), [previousYear - 1]: getPreviousTotals(String(previousYear - 1)) }
        }
        setPreviousTotals(tempPreviousTotals)
    }, [getPreviousTotals, isInternal])

    useEffect(() => {
        if (saveData.saving) {
            clearErrors()
            const triggerSpectral = isInternal || direction !== "backward"

            const incomeAttributes = (incomeAttributesState.data as Record<string, unknown>[]).filter((data) => data.value !== null || data.description !== null || data.percent !== null);
            dispatch(putIncomeAttributesBySnapshotId(currentSnapshotId, {
                "attributes": incomeAttributes,
                "spectral_section": profileOverview.profile.profile_type_code.toLowerCase() + "-" + menuState.activeItem.name,
                "trigger_spectral": triggerSpectral,
                "is_internal": isInternal
            }));
        }
    }, [currentSnapshotId, dispatch, incomeAttributesState.data, menuState.activeItem.name, saveData.saving, profileOverview.profile.profile_type_code, clearErrors, direction, isInternal])

    useEffect(() => {
        if (saveResultState.status === 200) {
            dispatch(getIncomeAttributesBySnapshotId(currentSnapshotId));
        }
    }, [currentSnapshotId, dispatch, saveResultState]);

    useEffect(() => {
        if (!lookupState.success) {
            dispatch(getLookups(lookupType));
        }
    }, [dispatch, lookupState.success, lookupType]);

    const findAttributeData = (attributeCode: string) => {
        let incomeAttributesData: Record<string, unknown> | undefined = undefined;
        if (incomeAttributesState.data) {
            incomeAttributesData = (incomeAttributesState.data as Record<string, unknown>[]).find(({ code }) => code === attributeCode);
        }
        return incomeAttributesData;
    };

    const handleCurrencyChange = (newAmt: number | undefined | null, name: string, _oldAmt: number | undefined | null) => {
        const idx = name.lastIndexOf("-");
        if (idx !== -1) {
            const code = name.substring(idx + 1);
            const incomeAttributesData = findAttributeData(code);
            const na = (newAmt === undefined || newAmt === null) ? null : newAmt
            if (incomeAttributesData) {
                incomeAttributesData["value"] = na;
            } else {
                (incomeAttributesState.data as Record<string, unknown>[]).push({
                    snapshotId: currentSnapshotId,
                    code: code,
                    description: null,
                    value: na,
                    type: "3",
                    percent: null,
                    year: null,
                });
            }

            setTotal(calculateIncomeAttributesTotal(lookupState.lookups[3]["cost"] as IncomeAttributeLookupObject[],
                incomeAttributesState.data as Record<string, unknown>[]))
        }
    }

    const handleCommentChange = (value: string, name: string) => {
        const idx = name.lastIndexOf("-");
        if (idx !== -1) {
            const code = name.substring(idx + 1);
            const incomeAttributesData = findAttributeData(code);
            const v = value.trim() === "" ? null : value.trim();
            if (incomeAttributesData) {
                incomeAttributesData["description"] = v;
            } else {
                (incomeAttributesState.data as Record<string, unknown>[]).push({
                    snapshotId: currentSnapshotId,
                    code: code,
                    description: v,
                    value: null,
                    type: "3",
                    percent: null,
                    year: null
                });
            }
        }
    }

    return (
        <div className='full-width'>
            <div className='content-container'>
                <ErrorMessages errors={errors} />
                <p className='strong'>{t('costs.caption1', { ns: 'pie' })}</p>
                {(!incomeAttributesState.loading && !previousIncomeAttributesState.loading && !lookupState.loading) &&
                    <div className="accordion-root">
                        <div className={accordionDisplay(isInternal, "accordion")}>
                            <ControlledAccordion providerValue={providerValue}>
                                <AccordionItem header={t(menuState.activeItem.name, { ns: "navigation" })}
                                    ariaLabel={t('costs.totalCapitalCostSummary', { ns: 'pie' })}>
                                    <CapitalCostsSummaryTab
                                        lookupType={lookupType}
                                        handleCommentChange={handleCommentChange}
                                        handleCurrencyChange={handleCurrencyChange}
                                        incomeAttributes={(incomeAttributesState.data && (incomeAttributesState.data as Record<string, unknown>[]).length > 0) ? (incomeAttributesState.data as Record<string, unknown>[]) : undefined}
                                        lookups={lookupState.lookups[3]["cost"] ? (lookupState.lookups[3]["cost"] as IncomeAttributeLookupObject[]) : []}
                                        name={menuState.activeItem.name}
                                    />
                                </AccordionItem>
                            </ControlledAccordion>
                        </div>
                        {shouldDisplayPreviousYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='capital' lookupType={lookupType} forcedTab year={String(currentCampaignYear - 1)} /> : <></>}
                        {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <PreviousCampaignIncomeAttributes tabComponent='capital' lookupType={lookupType} forcedTab year={String(currentCampaignYear - 2)} /> : <></>}
                    </div>
                }
                <div className='total-container'>
                    <div className="accordion-root">
                        <div className={accordionDisplay(isInternal, "accordion")}>
                            <Total text={t('costs.totalCapitalExpSummary', { ns: 'pie' })} value={total} label={true} narrow={(shouldDisplayPreviousYearAttributes() || shouldDisplayPreviousTwoYearAttributes()) ? true : false} />
                        </div>
                        {shouldDisplayPreviousYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
                            <Total text={t('costs.totalCapitalExpSummary', { ns: 'pie' })} value={previousTotals[String(currentCampaignYear - 1)]} />
                        </div> : <></>}
                        {isInternal && shouldDisplayPreviousTwoYearAttributes() ? <div className={accordionDisplay(isInternal, "right")}>
                            <Total text={t('costs.totalCapitalExpSummary', { ns: 'pie' })} value={previousTotals[String(currentCampaignYear - 2)]} />
                        </div> : <></>}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default CapitalCostsSummary
